import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import apiService from "../../service/Api";
import { showSuccessToast, showErrorToast } from "../../components/common/ShowToast";
import { ChatSidebar } from "./components/ChatSidebar";
import { ChatThread } from "./components/ChatThread";
import { TemplateModal } from "./components/TemplateModal";
import { NewChatModal } from "./components/NewChatModal";
import { ChatThread as ChatThreadType, ChatMessage, WhatsAppTemplate } from "./types";

export const WhatsAppChatsPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchParams, setSearchParams] = useSearchParams();

  const [threads, setThreads] = useState<ChatThreadType[]>([]);
  const [selectedRecipient, setSelectedRecipient] = useState<string | null>(null);
  const selectedRecipientRef = useRef<string | null>(selectedRecipient);
  useEffect(() => {
    selectedRecipientRef.current = selectedRecipient;
  }, [selectedRecipient]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);

  const [loadingChats, setLoadingChats] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [newChatModalOpen, setNewChatModalOpen] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);

  // User idle & background tab tracking for smart battery/network-saving polling
  const [isIdle, setIsIdle] = useState(false);
  const isIdleRef = useRef(false);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Read recipient from URL query param if present e.g. /whatsapp-chats?phone=263771234567
  const initialPhone = searchParams.get("phone");

  // Fetch all chat threads
  const fetchChats = useCallback(async (silent = false) => {
    if (!silent) setLoadingChats(true);
    try {
      const res = await apiService.get<{ ok: boolean; chats: ChatThreadType[] }>(
        "/api/whatsapp/chats/",
        { params: searchQuery ? { q: searchQuery } : {} }
      );
      if (res.data?.ok) {
        setThreads(res.data.chats);

        // If no recipient selected yet, select first thread on desktop or initialPhone on mobile
        if (res.data.chats.length > 0) {
          setSelectedRecipient((prev) => {
            if (prev) return prev;
            if (initialPhone) {
              const matched = res.data.chats.find(
                (c) => c.recipient === initialPhone || c.formatted_phone.includes(initialPhone)
              );
              if (matched) return matched.recipient;
            }
            return isMobile ? null : res.data.chats[0].recipient;
          });
        }
      }
    } catch (err) {
      console.error("Failed to fetch WhatsApp chats:", err);
      if (!silent) {
        showErrorToast("Failed to load WhatsApp conversations.");
      }
    } finally {
      if (!silent) setLoadingChats(false);
    }
  }, [searchQuery, initialPhone, isMobile]);

  // Fetch messages for selected recipient
  const fetchMessages = useCallback(async (recipient: string, silent = false) => {
    if (!recipient) return;
    if (!silent) setLoadingMessages(true);
    try {
      const res = await apiService.get<{
        ok: boolean;
        recipient: string;
        formatted_phone: string;
        is_window_open: boolean;
        window_expires_at: string | null;
        contact_info: any;
        messages: ChatMessage[];
      }>(`/api/whatsapp/chats/${recipient}/messages/`);

      // Avoid applying stale response if user switched chats
      if (selectedRecipientRef.current !== recipient) return;

      if (res.data?.ok) {
        setMessages((prev) => {
          const newMsgs = res.data.messages || [];
          if (prev.length === newMsgs.length) {
            // Check if any message status or ID changed
            const isIdentical = prev.every((m, idx) => {
              const nm = newMsgs[idx];
              return (
                nm &&
                m.id === nm.id &&
                m.status === nm.status &&
                m.media_url === nm.media_url &&
                m.message === nm.message
              );
            });
            if (isIdentical) {
              return prev; // Preserve array identity: prevents scroll jump and unnecessary DOM redraws
            }
          }
          return newMsgs;
        });

        // Update thread window status & unread count locally
        setThreads((prev) =>
          prev.map((t) =>
            t.recipient === recipient
              ? {
                  ...t,
                  unread_count: 0,
                  is_window_open: res.data.is_window_open,
                  window_expires_at: res.data.window_expires_at,
                }
              : t
          )
        );
      }
    } catch (err) {
      console.error("Failed to fetch chat messages:", err);
      if (!silent) {
        showErrorToast("Failed to load message history.");
      }
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  }, []);

  // Fetch available WhatsApp templates
  const fetchTemplates = useCallback(async () => {
    try {
      const res = await apiService.get<{ ok: boolean; templates: WhatsAppTemplate[] }>(
        "/api/whatsapp/templates/"
      );
      if (res.data?.ok) {
        setTemplates(res.data.templates);
      }
    } catch (err) {
      console.error("Failed to load WhatsApp templates:", err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchChats();
    fetchTemplates();
  }, [fetchChats, fetchTemplates]);

  // When selected recipient changes, fetch messages
  useEffect(() => {
    if (selectedRecipient) {
      fetchMessages(selectedRecipient);
    } else {
      setMessages([]);
    }
  }, [selectedRecipient, fetchMessages]);

  // Function to perform one silent background refresh
  const pollSilentUpdates = useCallback(() => {
    if (typeof document !== "undefined" && document.hidden) return;
    if (isIdleRef.current) return;
    fetchChats(true);
    if (selectedRecipientRef.current) {
      fetchMessages(selectedRecipientRef.current, true);
    }
  }, [fetchChats, fetchMessages]);

  // Visibility and Idle-aware auto-refresh manager
  useEffect(() => {
    const IDLE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes of user inactivity

    const startPolling = () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = setInterval(() => {
        pollSilentUpdates();
      }, 10000);
    };

    const stopPolling = () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };

    const resetIdleTimer = () => {
      if (isIdleRef.current) {
        isIdleRef.current = false;
        setIsIdle(false);
        // User woke up from idle: immediately refresh and restart polling
        pollSilentUpdates();
        if (typeof document !== "undefined" && !document.hidden) {
          startPolling();
        }
      }
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        isIdleRef.current = true;
        setIsIdle(true);
        stopPolling();
      }, IDLE_TIMEOUT_MS);
    };

    // Tab visibility listener: immediately pause when hidden, immediately refresh when visible
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        if (!isIdleRef.current) {
          pollSilentUpdates();
          startPolling();
        }
      }
    };

    // User activity listeners
    const activityEvents = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    activityEvents.forEach((evt) =>
      window.addEventListener(evt, resetIdleTimer, { passive: true })
    );
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Initial setup
    resetIdleTimer();
    if (typeof document !== "undefined" && !document.hidden) {
      startPolling();
    }

    return () => {
      stopPolling();
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      activityEvents.forEach((evt) =>
        window.removeEventListener(evt, resetIdleTimer)
      );
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [pollSilentUpdates]);

  // Send plain text message
  const handleSendMessage = async (text: string) => {
    if (!selectedRecipient || !text.trim()) return;

    setSending(true);
    try {
      const res = await apiService.post<{
        ok: boolean;
        message?: string;
        data?: ChatMessage;
        error?: string;
      }>(`/api/whatsapp/chats/${selectedRecipient}/send/`, {
        message: text,
        message_type: "text",
      });

      if (res.data?.ok) {
        showSuccessToast("Message sent to WhatsApp!");
        if (res.data.data) {
          setMessages((prev) => [...prev, res.data.data!]);
        }
        fetchChats(true);
      } else {
        showErrorToast(res.data?.error || "Failed to send message.");
      }
    } catch (err: any) {
      console.error("Error sending chat message:", err);
      const msg = err.response?.data?.error || err.message || "Failed to send message.";
      showErrorToast(msg);
    } finally {
      setSending(false);
    }
  };

  // Send template message
  const handleSendTemplate = async (
    templateName: string,
    variables: string[],
    lang = "en",
    renderedBody = "",
    imageB64?: string
  ) => {
    if (!selectedRecipient) return;

    setSending(true);
    try {
      const payload: any = {
        message_type: "template",
        template_name: templateName,
        template_variables: variables,
        template_lang: lang,
        message: renderedBody,
      };
      if (imageB64) {
        payload.image_b64 = imageB64;
      }

      const res = await apiService.post<{
        ok: boolean;
        message?: string;
        data?: ChatMessage;
        error?: string;
      }>(`/api/whatsapp/chats/${selectedRecipient}/send/`, payload);

      if (res.data?.ok) {
        showSuccessToast("WhatsApp template sent successfully!");
        if (res.data.data) {
          setMessages((prev) => [...prev, res.data.data!]);
        }
        fetchChats(true);
      } else {
        showErrorToast(res.data?.error || "Failed to send template.");
      }
    } catch (err: any) {
      console.error("Error sending template:", err);
      const msg = err.response?.data?.error || err.message || "Failed to send template.";
      showErrorToast(msg);
    } finally {
      setSending(false);
    }
  };

  // Start new chat with phone number
  const handleStartNewChat = (phone: string, name?: string) => {
    setSelectedRecipient(phone);
    const existing = threads.find((t) => t.recipient === phone);
    if (!existing) {
      const newThread: ChatThreadType = {
        recipient: phone,
        formatted_phone: phone.startsWith("263")
          ? `+263 ${phone.slice(3, 5)} ${phone.slice(5, 8)} ${phone.slice(8)}`
          : `+${phone}`,
        contact_name: name || `+${phone}`,
        contact_type: "individual",
        email: null,
        booking_id: null,
        booking_title: null,
        booking_service: null,
        last_message: "New conversation",
        last_message_time: new Date().toISOString(),
        last_message_direction: "outbound",
        last_message_status: "pending",
        unread_count: 0,
        is_window_open: false,
        window_expires_at: null,
      };
      setThreads((prev) => [newThread, ...prev]);
    }
    setSearchParams({ phone });
  };

  const selectedThread = threads.find((t) => t.recipient === selectedRecipient) || null;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        overflow: "hidden",
        backgroundColor: isDark ? "#0f172a" : "#F0F4F8",
        color: isDark ? "#F8FAFC" : "#0F172A",
      }}
    >
      {/* Left Sidebar */}
      {(!isMobile || !selectedRecipient) && (
        <Box
          sx={{
            width: { xs: "100%", md: 360, lg: 400 },
            flexShrink: 0,
            height: "100%",
          }}
        >
          <ChatSidebar
            threads={threads}
            selectedRecipient={selectedRecipient}
            onSelectThread={(t) => {
              setSelectedRecipient(t.recipient);
              setSearchParams({ phone: t.recipient });
            }}
            onOpenNewChat={() => setNewChatModalOpen(true)}
            onRefresh={() => fetchChats()}
            loading={loadingChats}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            isIdle={isIdle}
          />
        </Box>
      )}

      {/* Right Chat Thread */}
      {(!isMobile || selectedRecipient) && (
        <Box sx={{ flexGrow: 1, height: "100%", minWidth: 0 }}>
          <ChatThread
            thread={selectedThread}
            messages={messages}
            loadingMessages={loadingMessages}
            sending={sending}
            onSendMessage={handleSendMessage}
            onOpenTemplateModal={() => setTemplateModalOpen(true)}
            onRefreshMessages={() =>
              selectedRecipient && fetchMessages(selectedRecipient)
            }
            onBackToList={() => {
              setSelectedRecipient(null);
              setSearchParams({});
            }}
            isMobile={isMobile}
          />
        </Box>
      )}

      {/* Template Modal */}
      {selectedThread && (
        <TemplateModal
          open={templateModalOpen}
          onClose={() => setTemplateModalOpen(false)}
          templates={templates}
          recipientPhone={selectedThread.formatted_phone}
          contactName={selectedThread.contact_name}
          defaultDeceasedName={
            selectedThread.booking_title?.includes(" - ")
              ? selectedThread.booking_title.split(" - ")[1]
              : ""
          }
          onSendTemplate={handleSendTemplate}
          sending={sending}
        />
      )}

      {/* New Chat Modal */}
      <NewChatModal
        open={newChatModalOpen}
        onClose={() => setNewChatModalOpen(false)}
        onStartChat={handleStartNewChat}
      />
    </Box>
  );
};

export default WhatsAppChatsPage;
